import { doc, getDoc, setDoc, updateDoc, increment, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Feature limits for each membership tier
export const FEATURE_LIMITS = {
  free: {
    aiResponses: 2, // per day (TESTING: lowered from 20)
    documentUploads: 1, // per day (TESTING: lowered from 6)
    youtubeVideos: 1, // per day (TESTING: lowered from 2)
    flashcards: 'basic', // limited features
    quizSharing: 3, // max people to share with
    quizType: 'private', // only private quizzes
    support: 'community'
  },
  pro: {
    aiResponses: 'unlimited',
    documentUploads: 6, // per day
    flashcards: 'advanced',
    quizSharing: 'unlimited',
    quizType: 'live', // can publish live quizzes
    autoGrading: true,
    sellCourses: true,
    analytics: 'advanced',
    export: true,
    support: 'email'
  },
  enterprise: {
    aiResponses: 'unlimited',
    documentUploads: 'unlimited',
    flashcards: 'advanced',
    quizSharing: 'unlimited',
    quizType: 'live',
    autoGrading: true,
    sellCourses: true,
    analytics: 'advanced',
    export: true,
    support: 'priority',
    aiTutor: true, // real-time
    learningPaths: true,
    priorityProcessing: true
  }
};

// Get current date in YYYY-MM-DD format
export function getCurrentDate() {
  return new Date().toISOString().split('T')[0];
}

// Get current month in YYYY-MM format
export function getCurrentMonth() {
  return new Date().toISOString().substring(0, 7);
}

// Initialize user membership data
export async function initializeUserMembership(uid, email) {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        uid,
        email,
        membershipTier: 'free',
        subscriptionStatus: 'active',
        currentPeriodEnd: null,
        subscriptionId: null,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error initializing user membership:', error);
    // Return true even if there's an error to prevent blocking the app
    return true;
  }
}

// Get user membership data
export async function getUserMembership(uid) {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user membership:', error);
    return null;
  }
}

// Get daily usage for a specific date
export async function getDailyUsage(uid, date = getCurrentDate()) {
  try {
    // Create subcollection reference properly
    const usageRef = doc(db, 'users', uid, 'dailyUsage', date);
    const usageDoc = await getDoc(usageRef);
    
    if (usageDoc.exists()) {
      return usageDoc.data();
    }
    
    // Return default usage if no data exists
    return {
      aiResponses: 0,
      documentUploads: 0,
      quizGenerations: 0,
      flashcardGenerations: 0,
      chatMessages: 0
    };
  } catch (error) {
    console.error('Error getting daily usage:', error);
    return {
      aiResponses: 0,
      documentUploads: 0,
      quizGenerations: 0,
      flashcardGenerations: 0,
      chatMessages: 0
    };
  }
}

// Get monthly usage for a specific month
export async function getMonthlyUsage(uid, month = getCurrentMonth()) {
  try {
    // Create subcollection reference properly
    const usageRef = doc(db, 'users', uid, 'monthlyUsage', month);
    const usageDoc = await getDoc(usageRef);
    
    if (usageDoc.exists()) {
      return usageDoc.data();
    }
    
    // Return default usage if no data exists
    return {
      aiResponses: 0,
      documentUploads: 0,
      quizGenerations: 0,
      flashcardGenerations: 0,
      chatMessages: 0
    };
  } catch (error) {
    console.error('Error getting monthly usage:', error);
    return {
      aiResponses: 0,
      documentUploads: 0,
      quizGenerations: 0,
      flashcardGenerations: 0,
      chatMessages: 0
    };
  }
}

// Check if user can perform a specific action
export async function checkFeatureLimit(uid, feature, isMonthly = false) {
  try {
    const user = await getUserMembership(uid);
    if (!user) return false;
    
    const limits = FEATURE_LIMITS[user.membershipTier];
    if (!limits) return false;
    
    // Check if feature is unlimited
    if (limits[feature] === 'unlimited') return true;
    
    // Get usage data
    const usage = isMonthly ? 
      await getMonthlyUsage(uid) : 
      await getDailyUsage(uid);
    
    const currentUsage = usage[feature] || 0;
    const limit = limits[feature];
    
    return currentUsage < limit;
  } catch (error) {
    console.error('Error checking feature limit:', error);
    return false;
  }
}

// Increment usage for a specific feature
export async function incrementUsage(uid, feature, isMonthly = false) {
  try {
    const date = isMonthly ? getCurrentMonth() : getCurrentDate();
    const subcollectionName = isMonthly ? 'monthlyUsage' : 'dailyUsage';
    
    // Create subcollection reference properly
    const usageRef = doc(db, 'users', uid, subcollectionName, date);
    
    // Check if document exists first
    const usageDoc = await getDoc(usageRef);
    
    if (usageDoc.exists()) {
      // Document exists, update it
      await updateDoc(usageRef, {
        [feature]: increment(1),
        lastUpdated: new Date()
      });
    } else {
      // Document doesn't exist, create it
      await setDoc(usageRef, {
        [feature]: 1,
        lastUpdated: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error incrementing usage:', error);
    return false;
  }
}

// Get remaining usage for a feature
export async function getRemainingUsage(uid, feature, isMonthly = false) {
  try {
    const user = await getUserMembership(uid);
    if (!user) return 0;
    
    const limits = FEATURE_LIMITS[user.membershipTier];
    if (!limits) return 0;
    
    // Check if feature is unlimited
    if (limits[feature] === 'unlimited') return 'unlimited';
    
    // Get usage data
    const usage = isMonthly ? 
      await getMonthlyUsage(uid) : 
      await getDailyUsage(uid);
    
    const currentUsage = usage[feature] || 0;
    const limit = limits[feature];
    
    return Math.max(0, limit - currentUsage);
  } catch (error) {
    console.error('Error getting remaining usage:', error);
    return 0;
  }
}

// Get usage percentage for a feature
export async function getUsagePercentage(uid, feature, isMonthly = false) {
  try {
    const user = await getUserMembership(uid);
    if (!user) return 0;
    
    const limits = FEATURE_LIMITS[user.membershipTier];
    if (!limits) return 0;
    
    // Check if feature is unlimited
    if (limits[feature] === 'unlimited') return 0;
    
    // Get usage data
    const usage = isMonthly ? 
      await getMonthlyUsage(uid) : 
      await getDailyUsage(uid);
    
    const currentUsage = usage[feature] || 0;
    const limit = limits[feature];
    
    return Math.min(100, (currentUsage / limit) * 100);
  } catch (error) {
    console.error('Error getting usage percentage:', error);
    return 0;
  }
}

// Get all usage data for display
export async function getAllUsageData(uid) {
  try {
    const [user, dailyUsage, monthlyUsage] = await Promise.all([
      getUserMembership(uid),
      getDailyUsage(uid),
      getMonthlyUsage(uid)
    ]);
    
    if (!user) return null;
    
    const limits = FEATURE_LIMITS[user.membershipTier];
    
    // Calculate remaining usage and percentages
    const dailyRemaining = {};
    const monthlyRemaining = {};
    const dailyPercentage = {};
    const monthlyPercentage = {};
    
    Object.keys(limits).forEach(feature => {
      if (limits[feature] === 'unlimited') {
        dailyRemaining[feature] = 'unlimited';
        monthlyRemaining[feature] = 'unlimited';
        dailyPercentage[feature] = 0;
        monthlyPercentage[feature] = 0;
      } else {
        const dailyCurrent = dailyUsage[feature] || 0;
        const monthlyCurrent = monthlyUsage[feature] || 0;
        
        dailyRemaining[feature] = Math.max(0, limits[feature] - dailyCurrent);
        monthlyRemaining[feature] = Math.max(0, limits[feature] - monthlyCurrent);
        
        dailyPercentage[feature] = Math.min(100, (dailyCurrent / limits[feature]) * 100);
        monthlyPercentage[feature] = Math.min(100, (monthlyCurrent / limits[feature]) * 100);
      }
    });
    
    return {
      user,
      limits,
      dailyUsage,
      monthlyUsage,
      dailyRemaining,
      monthlyRemaining,
      dailyPercentage,
      monthlyPercentage
    };
  } catch (error) {
    console.error('Error getting all usage data:', error);
    return null;
  }
}

// Update user membership tier
export async function updateUserMembership(uid, newTier, subscriptionId = null) {
  try {
    const userRef = doc(db, 'users', uid);
    
    await updateDoc(userRef, {
      membershipTier: newTier,
      subscriptionId,
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating user membership:', error);
    return false;
  }
}
